import { createId } from "./ids";
import type { ContactEnrichment, MetAt } from "./types";

// ─────────────────────────────────────────────────────────────
// The loop entities (per omina-v1-build-spec §6):
// Contact + Move + Outcome — the private data that IS the moat.
// Rule (spec §8.6): logging must be one tap max, or the loop breaks.
//
// These are PURE reducers: (state, event) → state. The prototype's
// loadLoop/saveLoop pair lived here too and read localStorage — that
// persistence layer is gone (D1 + Drizzle owns storage now), and with it
// the last reason this file knew what a browser was. Keeping the reducers
// pure is what makes the loop testable without a runtime.
// ─────────────────────────────────────────────────────────────

export type ContactState = "new" | "contacted" | "replied" | "warm" | "converted";
export type OutcomeResult = "no_reply" | "replied" | "meeting" | "converted";

export interface Contact {
  id: string;
  name: string;
  /** where/how they met — the human context the draft is built on */
  context: string;
  company?: string;
  role?: string;
  email?: string;      // enables one-click Gmail send
  linkedinUrl?: string;
  researchNotes?: string;
  /** ISO date the LinkedIn connection was made, when they came from an
   *  import. What gets crossed with the calendar to find the room. */
  connectedOn?: string;
  /** The room, once she has confirmed it. Absent means unplaced — a normal
   *  state, and the one the circle asks about. */
  metAt?: MetAt;
  /** Structured public research, superseding the flat `researchNotes`. */
  enrichment?: ContactEnrichment;
  /** When `enrichment` was produced — staleness is visible, not guessed. */
  researchedAt?: string;
  /** ISO date or bare MM-DD. Hers to type; nothing fills it automatically. */
  birthday?: string;
  /** Lowercased chips she applied. Never a stored guess. */
  labels?: string[];
  state: ContactState;
  createdAt: string;
}

export interface Move {
  id: string;
  contactId: string;
  type: "follow_up";
  draft: string;
  channel: "email";
  /** Gmail thread — the reply-detection handle (spec §5) */
  threadId?: string;
  sentAt?: string;
}

export interface Outcome {
  id: string;
  moveId: string;
  result: OutcomeResult;
  source: "manual" | "inbox_auto";
  capturedAt: string;
}

export interface LoopData {
  contacts: Contact[];
  moves: Move[];
  outcomes: Outcome[];
}

/** An empty loop — the starting state for a brand-new org. */
export const emptyLoop = (): LoopData => ({ contacts: [], moves: [], outcomes: [] });

export function addContact(
  d: LoopData,
  c: Omit<Contact, "id" | "state" | "createdAt">
): { data: LoopData; contact: Contact } {
  const contact: Contact = { ...c, id: createId(), state: "new", createdAt: new Date().toISOString() };
  return { data: { ...d, contacts: [contact, ...d.contacts] }, contact };
}

export function recordMove(
  d: LoopData,
  contactId: string,
  draft: string,
  threadId?: string
): { data: LoopData; move: Move } {
  const move: Move = {
    id: createId(),
    contactId,
    type: "follow_up",
    draft,
    channel: "email",
    threadId,
    sentAt: new Date().toISOString(),
  };
  const contacts = d.contacts.map((c) =>
    c.id === contactId && c.state === "new" ? { ...c, state: "contacted" as ContactState } : c
  );
  return { data: { ...d, contacts, moves: [move, ...d.moves] }, move };
}

/** Outcome → the contact state it implies. Exported: the UI colours the
 *  contact row from the same table the reducer uses. */
export const STATE_FOR: Record<OutcomeResult, ContactState> = {
  no_reply: "contacted",
  replied: "replied",
  meeting: "warm",
  converted: "converted",
};

/** One-tap outcome log — updates the contact state and appends the outcome. */
export function logOutcome(
  d: LoopData,
  contactId: string,
  result: OutcomeResult,
  source: Outcome["source"] = "manual"
): LoopData {
  const move = d.moves.find((m) => m.contactId === contactId);
  const outcome: Outcome = {
    id: createId(),
    moveId: move?.id ?? "manual",
    result,
    source,
    capturedAt: new Date().toISOString(),
  };
  const contacts = d.contacts.map((c) =>
    c.id === contactId ? { ...c, state: STATE_FOR[result] } : c
  );
  return { ...d, contacts, outcomes: [outcome, ...d.outcomes] };
}

/** The compounding arrow (spec §8.1): what the brain learned from outcomes.
 *  Fed into every new draft — which moves landed, which didn't. */
export function outcomeSummary(d: LoopData): string {
  const byMove = new Map(d.moves.map((m) => [m.id, m]));
  const lines: string[] = [];
  for (const o of d.outcomes.slice(0, 12)) {
    const m = byMove.get(o.moveId);
    if (!m) continue;
    const c = d.contacts.find((x) => x.id === m.contactId);
    const firstLine = m.draft.split("\n").find((l) => l.trim()) ?? "";
    lines.push(
      `- ${o.result.toUpperCase()} — to ${c?.name ?? "?"} (${c?.role ?? "?"} @ ${c?.company ?? "?"}): "${firstLine.slice(0, 90)}"`
    );
  }
  return lines.length ? lines.join("\n") : "";
}
